import React, { useEffect, useState, useRef } from 'react';
import { InfoIcon, ZapIcon, BrainIcon, ActivityIcon } from 'lucide-react';
export const Portfolio = ({
  onCursorChange
}) => {
  const [activeProject, setActiveProject] = useState(null);
  const [projectData, setProjectData] = useState({});
  const projectRefs = useRef([]);
  const sectionRef = useRef(null);
  useEffect(() => {
    // Animation on scroll
    const observer = new IntersectionObserver(entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Staggered animation delay
          setTimeout(() => {
            entry.target.classList.add('animate-in');
          }, index * 200);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    });
    // Observe all project elements
    projectRefs.current.forEach(project => {
      if (project) observer.observe(project);
    });
    // Generate random project data
    const data = {};
    projects.forEach((project, index) => {
      data[index] = {
        subjects: Math.floor(Math.random() * 100) + 1,
        mutations: Math.floor(Math.random() * 200) + 50,
        viability: `${Math.floor(Math.random() * 30) + 70}%`,
        corruption: ['HIGH', 'EXTREME', 'CRITICAL', 'UNSTABLE'][Math.floor(Math.random() * 4)],
        status: ['ACTIVE', 'STABLE', 'EVOLVING'][Math.floor(Math.random() * 3)],
        phase: `${Math.floor(Math.random() * 5) + 1}.${Math.floor(Math.random() * 10)}`
      };
    });
    setProjectData(data);
    return () => {
      projectRefs.current.forEach(project => {
        if (project) observer.unobserve(project);
      });
    };
  }, []);
  // Handle project interaction
  const handleProjectClick = index => {
    if (activeProject === index) {
      setActiveProject(null);
    } else {
      setActiveProject(index);
    }
  };
  // Handle mouse movement for 3D card effect
  const handleMouseMove = (e, index) => {
    if (!projectRefs.current[index]) return;
    const card = projectRefs.current[index];
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Calculate position relative to center
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 25;
    const rotateY = (centerX - x) / 25;
    // Apply rotation
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    // Update highlight position
    const highlight = card.querySelector('.card-highlight');
    if (highlight) {
      highlight.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(94, 231, 223, 0.2) 0%, rgba(94, 231, 223, 0) 50%)`;
    }
  };
  // Reset card on mouse leave
  const handleMouseLeave = index => {
    if (!projectRefs.current[index]) return;
    const card = projectRefs.current[index];
    card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    const highlight = card.querySelector('.card-highlight');
    if (highlight) {
      highlight.style.background = 'none';
    }
    onCursorChange('default');
  };
  const projects = [{
    title: 'SYMBIOSIS',
    category: 'Living Interface',
    description: 'Parasitic installation extracting biometric data through direct contact.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8S4iAdBZMArMGUFJgy83fi/Screenshot_2025-08-27_at_10.25.07_AM.png'
  }, {
    title: 'MYCELIAL',
    category: 'Neural Extraction',
    description: 'Fungal networks transmitting distorted consciousness.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a88WA1soYYyKfaMetsd3nX/Screenshot_2025-08-27_at_9.46.42_AM.png'
  }, {
    title: 'CLIMATE DECAY',
    category: 'Data Organism',
    description: 'Environmental data as sensory violations of ecological collapse.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/13Qo1Lu5qLN8t3JKr8yDKX/Screenshot_2025-08-27_at_9.47.03_AM.png'
  }, {
    title: 'SYNTHETIC',
    category: 'Digital Infection',
    description: 'System creating autonomous evolving life forms.',
    image: 'https://images.unsplash.com/photo-1504270997636-07ddfbd48945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
  }, {
    title: 'FLESH INTERFACE',
    category: 'Biomaterial',
    description: 'Synthetic tissue responding to digital signals.',
    image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
  }, {
    title: 'VIRAL',
    category: 'Computational Organism',
    description: 'Structures using algorithms that mimic cancerous growth.',
    image: 'https://images.unsplash.com/photo-1567095761054-7a02e69e5c43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
  }];
  return <section id="portfolio" className="w-full py-24 bg-[#060a10] relative" ref={sectionRef} data-id="element-1120">
      <style jsx global data-id="element-1121">{`
        @keyframes project-reveal {
          0% {
            opacity: 0;
            transform: translateY(50px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .project-card {
          opacity: 0;
          transform: translateY(50px);
          transition: transform 0.3s ease-out;
          transform-style: preserve-3d;
        }
        .project-card.animate-in {
          animation: project-reveal 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)
            forwards;
        }
        .card-highlight {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 10;
          opacity: 0.8;
        }
        @keyframes data-scan {
          0% {
            height: 0;
            opacity: 0.8;
          }
          100% {
            height: 100%;
            opacity: 0;
          }
        }
        .data-scan {
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 0;
          background: linear-gradient(
            to top,
            rgba(94, 231, 223, 0.3),
            transparent
          );
          animation: data-scan 2.5s ease-in-out infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .project-expanded .data-scan {
          opacity: 1;
        }
        .data-point {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.65rem;
        }
        .data-point-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 2px;
          background: rgba(94, 231, 223, 0.1);
          color: #5ee7df;
        }
        .data-point-value {
          color: #e64cff;
        }
        .tech-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image:
            linear-gradient(
              to right,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
        }
        .project-expanded .tech-overlay {
          opacity: 0.2;
        }
        .interface-corner {
          position: absolute;
          width: 15px;
          height: 15px;
          border-color: rgba(94, 231, 223, 0.7);
          opacity: 0;
          transition: all 0.3s ease;
        }
        .corner-tl {
          top: 5px;
          left: 5px;
          border-top: 1px solid;
          border-left: 1px solid;
        }
        .corner-tr {
          top: 5px;
          right: 5px;
          border-top: 1px solid;
          border-right: 1px solid;
        }
        .corner-bl {
          bottom: 5px;
          left: 5px;
          border-bottom: 1px solid;
          border-left: 1px solid;
        }
        .corner-br {
          bottom: 5px;
          right: 5px;
          border-bottom: 1px solid;
          border-right: 1px solid;
        }
        .project-expanded .interface-corner {
          opacity: 1;
          width: 25px;
          height: 25px;
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-1122"></div>
      <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/hnoZmbdxw9bJJY53Mtbq9z/yacht_logo.png')] bg-center bg-no-repeat bg-cover opacity-5 mix-blend-color-burn" data-id="element-1123"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-1124">
        <div className="text-center mb-16" data-id="element-1125">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading animate-fade-in-up" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1126">
            EXPERIMENTS
          </h2>
          <p className="text-[#8a9cb9] max-w-2xl mx-auto font-mono animate-fade-in-up" style={{
          animationDelay: '0.2s'
        }} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1127">
            Disturbing natural order through bio-tech corruption. Creating forms
            that shouldn't exist.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1128">
          {projects.map((project, index) => <div key={index} ref={el => projectRefs.current[index] = el} className={`
                project-card relative overflow-hidden rounded-lg
                glass border border-[#1a2a40]/50 hover:border-[#5ee7df]/50 
                transition-all duration-500 group cursor-pointer
                ${activeProject === index ? 'project-expanded z-20' : activeProject !== null ? 'opacity-60' : ''}
              `} onClick={() => handleProjectClick(index)} onMouseMove={e => handleMouseMove(e, index)} onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => handleMouseLeave(index)} style={{
          animationDelay: `${index * 100}ms`
        }} data-id="element-1129">
              <div className="card-highlight" data-id="element-1130"></div>
              <div className="tech-overlay" data-id="element-1131"></div>
              {/* Interface corners */}
              <div className="interface-corner corner-tl" data-id="element-1132"></div>
              <div className="interface-corner corner-tr" data-id="element-1133"></div>
              <div className="interface-corner corner-bl" data-id="element-1134"></div>
              <div className="interface-corner corner-br" data-id="element-1135"></div>
              <div className="aspect-w-16 aspect-h-9 bg-[#000000]" data-id="element-1136">
                <img src={project.image} alt={project.title} className={`
                    w-full h-64 object-cover filter contrast-125 brightness-75
                    transition-all duration-700
                    ${activeProject === index ? 'grayscale-0 scale-110' : 'grayscale group-hover:grayscale-0 group-hover:scale-105'}
                  `} data-id="element-1137" />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/50 to-transparent opacity-70" data-id="element-1138"></div>
                {/* Scanning effect when active */}
                <div className="data-scan" data-id="element-1139"></div>
                {/* Scan line effect */}
                <div className={`
                  absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] 
                  bg-[size:100%_4px] animate-[scanlines_8s_linear_infinite] transition-opacity duration-500
                  ${activeProject === index ? 'opacity-60' : 'opacity-0 group-hover:opacity-40'}
                `} data-id="element-1140"></div>
              </div>
              <div className={`
                absolute inset-0 bg-[#000000]/90 flex flex-col justify-end p-6 
                transition-all duration-500
                ${activeProject === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
              `} data-id="element-1141">
                <span className="text-sm text-[#5ee7df] mb-2 font-mono uppercase tracking-wider" data-id="element-1142">
                  {project.category}
                </span>
                <h3 className="text-xl text-[#e0e6f0] font-bold mb-2 font-heading" data-id="element-1143">
                  {project.title}
                </h3>
                <p className="text-[#8a9cb9] text-sm font-mono" data-id="element-1144">
                  {project.description}
                </p>
                {/* Animated data points */}
                {activeProject === index && projectData[index] && <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-1145">
                    <div className="data-point" data-id="element-1146">
                      <div className="data-point-icon" data-id="element-1147">
                        <InfoIcon size={10} data-id="element-1148" />
                      </div>
                      <span data-id="element-1149">SUBJECTS:</span>
                      <span className="data-point-value" data-id="element-1150">
                        {projectData[index].subjects}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-1151">
                      <div className="data-point-icon" data-id="element-1152">
                        <ZapIcon size={10} data-id="element-1153" />
                      </div>
                      <span data-id="element-1154">MUTATIONS:</span>
                      <span className="data-point-value" data-id="element-1155">
                        {projectData[index].mutations}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-1156">
                      <div className="data-point-icon" data-id="element-1157">
                        <BrainIcon size={10} data-id="element-1158" />
                      </div>
                      <span data-id="element-1159">VIABILITY:</span>
                      <span className="data-point-value" data-id="element-1160">
                        {projectData[index].viability}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-1161">
                      <div className="data-point-icon" data-id="element-1162">
                        <ActivityIcon size={10} data-id="element-1163" />
                      </div>
                      <span data-id="element-1164">CORRUPTION:</span>
                      <span className="data-point-value" data-id="element-1165">
                        {projectData[index].corruption}
                      </span>
                    </div>
                    <div className="data-point col-span-2 mt-2 bg-[#0a1018]/50 p-2 rounded" data-id="element-1166">
                      <span data-id="element-1167">STATUS:</span>
                      <span className="data-point-value" data-id="element-1168">
                        {projectData[index].status}
                      </span>
                      <span className="ml-2" data-id="element-1169">PHASE:</span>
                      <span className="data-point-value" data-id="element-1170">
                        {projectData[index].phase}
                      </span>
                    </div>
                  </div>}
              </div>
              {/* Specimen label */}
              <div className={`
                absolute bottom-0 left-0 right-0 p-4 glass
                transform transition-transform duration-500
                ${activeProject === index || activeProject !== null && activeProject !== index ? 'translate-y-full' : 'translate-y-0 group-hover:translate-y-full'}
              `} data-id="element-1171">
                <h3 className="text-[#e0e6f0] font-bold font-heading" data-id="element-1172">
                  {project.title}
                </h3>
                <span className="text-xs text-[#5ee7df] font-mono" data-id="element-1173">
                  {project.category}
                </span>
              </div>
              {/* Glow effect on hover */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-[#5ee7df]/30 to-[#2a4d7f]/30 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500 -z-10 rounded-lg" data-id="element-1174"></div>
            </div>)}
        </div>
      </div>
    </section>;
};